import { createContext, useCallback, useContext, useState } from 'react';
import type { Alert, AuditLog, Building, Device, MeterReading, PaginatedResponse, Property, Unit, User } from '@/types/api';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

type CacheKey = 'properties' | 'buildings' | 'units' | 'devices' | 'alerts' | 'users' | 'audit-logs' | 'meter-readings';

interface DataCacheState {
  propertiesPaged: Record<string, CacheEntry<PaginatedResponse<Property>>>;
  buildingsPaged: Record<string, CacheEntry<PaginatedResponse<Building>>>;
  unitsPaged: Record<string, CacheEntry<PaginatedResponse<Unit>>>;
  devicesPaged: Record<string, CacheEntry<PaginatedResponse<Device>>>;
  alerts: CacheEntry<Alert[]> | null;
  usersPaged: Record<string, CacheEntry<PaginatedResponse<User>>>;
  auditLogs: Record<string, CacheEntry<PaginatedResponse<AuditLog>>>;
  meterReadingsPaged: Record<string, CacheEntry<PaginatedResponse<MeterReading>>>;
}

interface DataCacheContextType {
  getPropertiesPage: (key: string) => PaginatedResponse<Property> | null;
  setPropertiesPage: (key: string, data: PaginatedResponse<Property>) => void;
  getBuildingsPage: (key: string) => PaginatedResponse<Building> | null;
  setBuildingsPage: (key: string, data: PaginatedResponse<Building>) => void;
  getUnitsPage: (key: string) => PaginatedResponse<Unit> | null;
  setUnitsPage: (key: string, data: PaginatedResponse<Unit>) => void;
  getDevicesPage: (key: string) => PaginatedResponse<Device> | null;
  setDevicesPage: (key: string, data: PaginatedResponse<Device>) => void;
  getAlerts: () => Alert[] | null;
  getUsersPage: (key: string) => PaginatedResponse<User> | null;
  setUsersPage: (key: string, data: PaginatedResponse<User>) => void;
  getAuditLogsPage: (key: string) => PaginatedResponse<AuditLog> | null;
  setAlerts: (data: Alert[]) => void;
  setAuditLogsPage: (key: string, data: PaginatedResponse<AuditLog>) => void;
  getMeterReadingsPage: (key: string) => PaginatedResponse<MeterReading> | null;
  setMeterReadingsPage: (key: string, data: PaginatedResponse<MeterReading>) => void;
  invalidate: (keys: CacheKey[]) => void;
  clearCache: () => void;
}

export function makePaginatedCacheKey(params: Record<string, unknown>): string {
  const entries = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

function isFresh(entry: CacheEntry<unknown> | null | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < CACHE_TTL_MS;
}

function makeInitialState(): DataCacheState {
  return {
    propertiesPaged: {},
    buildingsPaged: {},
    unitsPaged: {},
    devicesPaged: {},
    alerts: null,
    usersPaged: {},
    auditLogs: {},
    meterReadingsPaged: {},
  };
}

const DataCacheContext = createContext<DataCacheContextType | null>(null);

export function DataCacheProvider({ children }: { children: React.ReactNode }) {
  const [cache, setCache] = useState<DataCacheState>(makeInitialState);

  const getPropertiesPage = useCallback(
    (key: string) => {
      const entry = cache.propertiesPaged[key] ?? null;
      return isFresh(entry) ? entry!.data : null;
    },
    [cache.propertiesPaged],
  );

  const setPropertiesPage = useCallback((key: string, data: PaginatedResponse<Property>) => {
    setCache(prev => ({
      ...prev,
      propertiesPaged: { ...prev.propertiesPaged, [key]: { data, fetchedAt: Date.now() } },
    }));
  }, []);

  const getBuildingsPage = useCallback(
    (key: string) => {
      const entry = cache.buildingsPaged[key] ?? null;
      return isFresh(entry) ? entry!.data : null;
    },
    [cache.buildingsPaged],
  );

  const setBuildingsPage = useCallback((key: string, data: PaginatedResponse<Building>) => {
    setCache(prev => ({
      ...prev,
      buildingsPaged: { ...prev.buildingsPaged, [key]: { data, fetchedAt: Date.now() } },
    }));
  }, []);

  const getUnitsPage = useCallback(
    (key: string) => {
      const entry = cache.unitsPaged[key] ?? null;
      return isFresh(entry) ? entry!.data : null;
    },
    [cache.unitsPaged],
  );

  const setUnitsPage = useCallback((key: string, data: PaginatedResponse<Unit>) => {
    setCache(prev => ({
      ...prev,
      unitsPaged: { ...prev.unitsPaged, [key]: { data, fetchedAt: Date.now() } },
    }));
  }, []);

  const getDevicesPage = useCallback(
    (key: string) => {
      const entry = cache.devicesPaged[key] ?? null;
      return isFresh(entry) ? entry!.data : null;
    },
    [cache.devicesPaged],
  );

  const setDevicesPage = useCallback((key: string, data: PaginatedResponse<Device>) => {
    setCache(prev => ({
      ...prev,
      devicesPaged: { ...prev.devicesPaged, [key]: { data, fetchedAt: Date.now() } },
    }));
  }, []);

  const getAlerts = useCallback(
    () => (isFresh(cache.alerts) ? cache.alerts!.data : null),
    [cache.alerts],
  );

  const getUsersPage = useCallback(
    (key: string) => {
      const entry = cache.usersPaged[key] ?? null;
      return isFresh(entry) ? entry!.data : null;
    },
    [cache.usersPaged],
  );

  const getAuditLogsPage = useCallback(
    (key: string) => {
      const entry = cache.auditLogs[key] ?? null;
      return isFresh(entry) ? entry!.data : null;
    },
    [cache.auditLogs],
  );

  const setAlerts = useCallback((data: Alert[]) => {
    setCache(prev => ({ ...prev, alerts: { data, fetchedAt: Date.now() } }));
  }, []);

  const setUsersPage = useCallback((key: string, data: PaginatedResponse<User>) => {
    setCache(prev => ({
      ...prev,
      usersPaged: { ...prev.usersPaged, [key]: { data, fetchedAt: Date.now() } },
    }));
  }, []);

  const setAuditLogsPage = useCallback((key: string, data: PaginatedResponse<AuditLog>) => {
    setCache(prev => ({
      ...prev,
      auditLogs: { ...prev.auditLogs, [key]: { data, fetchedAt: Date.now() } },
    }));
  }, []);

  const getMeterReadingsPage = useCallback(
    (key: string) => {
      const entry = cache.meterReadingsPaged[key] ?? null;
      return isFresh(entry) ? entry!.data : null;
    },
    [cache.meterReadingsPaged],
  );

  const setMeterReadingsPage = useCallback((key: string, data: PaginatedResponse<MeterReading>) => {
    setCache(prev => ({
      ...prev,
      meterReadingsPaged: { ...prev.meterReadingsPaged, [key]: { data, fetchedAt: Date.now() } },
    }));
  }, []);

  const invalidate = useCallback((keys: CacheKey[]) => {
    setCache(prev => {
      const next = { ...prev };
      for (const key of keys) {
        if (key === 'properties') next.propertiesPaged = {};
        else if (key === 'buildings') next.buildingsPaged = {};
        else if (key === 'units') next.unitsPaged = {};
        else if (key === 'devices') next.devicesPaged = {};
        else if (key === 'alerts') next.alerts = null;
        else if (key === 'users') next.usersPaged = {};
        else if (key === 'audit-logs') next.auditLogs = {};
        else if (key === 'meter-readings') next.meterReadingsPaged = {};
      }
      return next;
    });
  }, []);

  const clearCache = useCallback(() => {
    setCache(makeInitialState());
  }, []);

  return (
    <DataCacheContext.Provider
      value={{
        getPropertiesPage,
        setPropertiesPage,
        getBuildingsPage,
        setBuildingsPage,
        getUnitsPage,
        setUnitsPage,
        getDevicesPage,
        setDevicesPage,
        getAlerts,
        getUsersPage,
        setUsersPage,
        getAuditLogsPage,
        setAlerts,
        setAuditLogsPage,
        getMeterReadingsPage,
        setMeterReadingsPage,
        invalidate,
        clearCache,
      }}
    >
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache(): DataCacheContextType {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within DataCacheProvider');
  }
  return context;
}
